// In this case the usage of any type is a must internally in the store so it can be generic, externally all types are well defined.
import { create } from "zustand";
import { State } from "@progress/kendo-data-query";
import { IDataGridRowState } from "@presentation/common/types/IDataGridRowState";
import { IEntitiy } from "@domain/entities";
import { SelectedState } from "@presentation/common/types/DataGridSelectedState";
import { IDataGridProgressRowState } from "../types/IDataGridProgressRowState";

/**
 * State of each detail
 */
export type DetailState<T1 extends IDataGridRowState> = {
  detailList: T1[];
  metaState: State;
  selectedState: SelectedState;
};

/**
 * Dictionary with key the id of the master row and the list with the details associated to the master row as value (see kendo master-detail grid docu for more info).
 */
export type DetailDictionary<T1 extends IDataGridRowState> = {
  [masterId: string]: DetailState<T1>;
};

/**
 * Definition of a store for Kendo Grids with selection, detail and state management. In this store the details is a list.
 */
export interface DetailDataGridStore<T1 extends IDataGridRowState, T2 extends IEntitiy<string>> {
  defaultMetaState: State;
  detailDictionary: DetailDictionary<T1>;
  /**
   * Method to add a list of details under a master ID
   * @param masterId The Id of the master object of the details
   * @param newDetailList The list of objects composing the master details
   */
  setDetailList: (masterId: string, newDetailList: T2[]) => void;
  /**
   * Set the is expanded property of a given object from the detail object(which is a list of objects), in order to achieve that it is required to provied the master object Id and the Id of the object from the detail list object to update the isExpanded property.
   * @param masterId The Id of the master object of the detail objects to update the isExpanded property
   * @param detailId The Id of the detail object to update the isExpanded property
   * @param isExpanded The new boolean value for the isExpanded property
   * @returns
   */
  setIsExpanded: (masterId: string, detailId: string, isExpanded: boolean) => void;
  /**
   * Delete from the store all the detail of the given Id master object.
   * @param masterId Id of the master object.
   * @returns
   */
  deleteDetail: (masterId: string) => void;
  /**
   * Sets the SelectedState of the detail
   * @param masterId
   * @param newSelectedState
   * @returns
   */
  setSelectedStateForDetail: (masterId: string, newSelectedState: SelectedState) => void;
  /**
   * Sets the MetaState of the detail
   * @param masterId
   * @param newMetaState
   * @returns
   */
  setMetaStateForDetail: (masterId: string, newMetaState: State) => void;
  setProgress: (masterId: string, detailId: string, progress: number) => void;
  getSessionId: () => string;
}

/**
 * Factory function to create a new store instance.
 */
export const createDetailDataGridStore = <T1 extends IDataGridProgressRowState, T2 extends IEntitiy<string>>() =>
  create<DetailDataGridStore<T1, T2>>((set) => ({
    defaultMetaState: {
      // sort: [{ field: "status", dir: "desc" }],
      // filter: {
      //   logic: "and",
      //   filters: [{ field: "task", operator: "contains", value: "" }],
      // },
    },

    detailDictionary: {},

    // Method to add a list of details under a master ID
    setDetailList: (masterId: string, newDetailList: any[]) => {
      set((state) => {
        const currentDetail = state.detailDictionary[masterId];
        // Check if the master ID already exists in the dictionary
        if (currentDetail) {
          // Update only the detailList for the existing master ID
          const updatedDetailList = newDetailList.map((detail) => ({
            ...detail,
            isSelected: currentDetail.detailList.some((t) => t.id === detail.id && t.isSelected),
            isExpanded: currentDetail.detailList.some((t) => t.id === detail.id && t.isExpanded),
          }));

          return {
            detailDictionary: {
              ...state.detailDictionary,
              [masterId]: {
                ...currentDetail,
                detailList: updatedDetailList,
              },
            },
          };
        }

        // Create a new entry for the master ID
        const newDetailState = {
          detailList: newDetailList,
          metaState: state.defaultMetaState,
          selectedState: {},
        };

        return {
          detailDictionary: {
            ...state.detailDictionary,
            [masterId]: newDetailState,
          },
        };
      });
    },

    setIsExpanded: (masterId: string, detailId: string, isExpanded: boolean) => {
      set((state) => {
        const updatedDetailDictionary = { ...state.detailDictionary };
        const detail = updatedDetailDictionary[masterId];
        if (detail) {
          const updatedDetailList = detail.detailList.map((d) => (d.id === detailId ? { ...d, isExpanded } : d));
          updatedDetailDictionary[masterId] = {
            ...detail,
            detailList: updatedDetailList,
          };
        }
        return { detailDictionary: updatedDetailDictionary };
      });
    },

    // Method to remove a list of detail under a master ID
    deleteDetail: (masterId: string) => {
      set((state) => {
        const updatedDictionary = { ...state.detailDictionary };
        delete updatedDictionary[masterId]; // Remove the entry for the master ID
        return { detailDictionary: updatedDictionary };
      });
    },

    // Method to set the selected state for detail of a given master ID
    setSelectedStateForDetail: (masterId: string, newSelectedState: SelectedState) => {
      set((state) => {
        const detail = state.detailDictionary[masterId];
        if (detail) {
          return {
            detailDictionary: {
              ...state.detailDictionary,
              [masterId]: {
                ...detail,
                selectedState: newSelectedState,
              },
            },
          };
        }
        // Return the current state if masterId is not found
        return state;
      });
    },

    setMetaStateForDetail: (masterId: string, newMetaState: State) => {
      set((state) => {
        const detail = state.detailDictionary[masterId];
        if (detail) {
          return {
            detailDictionary: {
              ...state.detailDictionary,
              [masterId]: {
                ...detail,
                metaState: newMetaState,
              },
            },
          };
        }
        // Return the current state if masterId is not found
        return state;
      });
    },

    setProgress: (masterId: string, detailId: string, progress: number) => {
      set((state) => {
        const updatedDetailDictionary = { ...state.detailDictionary };
        const detail = updatedDetailDictionary[masterId];
        if (detail) {
          const updatedDetailList = detail.detailList.map((d) => {
            return d.id === detailId ? { ...d, progress: progress } : d;
          });
          updatedDetailDictionary[masterId] = {
            ...detail,
            detailList: updatedDetailList,
          };
        }
        return { detailDictionary: updatedDetailDictionary };
      });
    },

    getSessionId: () => {
      const userSessionId = sessionStorage.getItem("userSessionId") || crypto.randomUUID();
      sessionStorage.setItem("userSessionId", userSessionId);
      return userSessionId;
    },
  }));
